import {
    ForwardedRef,
    forwardRef,
    InputHTMLAttributes,
    ReactNode,
    SyntheticEvent,
    useImperativeHandle,
    useRef,
} from 'react'
import { clsx } from 'clsx'

import styles from './Input.module.scss'

import CrossIcon from './assets/cross.svg'

export type Props = {
    transparent?: boolean
    variant?: 'small' | 'large' | 'table'
    allowClear?: boolean
    fullWidth?: boolean
    iconClassName?: string
    customAction?: ReactNode
    hidePlaceholderOnFocus?: boolean
} & InputHTMLAttributes<HTMLInputElement>

export const Input = forwardRef(
    (
        {
            variant = 'large',
            fullWidth = false,
            allowClear = false,
            transparent = false,
            iconClassName,
            className,
            customAction,
            hidePlaceholderOnFocus = false,
            ...props
        }: Props,
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        const inputRef = useRef<HTMLInputElement>(null)
        useImperativeHandle(ref, () => inputRef.current!, [])

        const handleClear = (event: SyntheticEvent) => {
            event.stopPropagation()
            const inputEl = inputRef.current

            if (!inputEl) {
                return
            }

            inputEl.value = ''

            if (props.onChange) {
                const event = new Event('input', { bubbles: true })
                Object.defineProperty(event, 'target', { writable: false, value: inputEl })
                props.onChange(event as unknown as React.ChangeEvent<HTMLInputElement>)
            }
        }

        return (
            <div className={styles.inputWrapper}>
                <input
                    {...props}
                    className={clsx(
                        styles.input,
                        styles[variant],
                        { [styles.fullWidth]: fullWidth },
                        { [styles.transparent]: transparent },
                        { [styles.hidePlaceholder]: hidePlaceholderOnFocus },
                        className
                    )}
                    ref={inputRef}
                    autoComplete="off"
                    onClick={(event) => event.stopPropagation()}
                />
                {allowClear && (
                    <button
                        type="button"
                        className={clsx(styles.crossButton, iconClassName)}
                        onClick={handleClear}
                        onMouseDown={(event) => event.preventDefault()}
                    >
                        <CrossIcon />
                    </button>
                )}
                {customAction && <div className={styles.customAction}>{customAction}</div>}
            </div>
        )
    }
)
